import React, { useState, useEffect, useContext } from "react";
import { unixToWin, winToUnix } from '../../shared/utils/os';
import { Modal, ModalButtonID, Select, Password, Checkbox } from '../../ui/components';
import { useForm, Controller } from "react-hook-form";
import { parse } from '../../utils/path';
import classNames from "classnames";
import { evolve, trim } from 'ramda';
import { themes } from '../Settings/Settings';
import { AppSettingsContext } from '../../context/config';
import styles from './Connection.less';
import themesStyle from '../Settings/Settings.less';
const protocols = [
    {
        value: 'sftp',
        label: 'SFTP - SSH File Transfer Protocol'
    },
    {
        value: 'ftp',
        label: 'FTP - File Transfer Protocol'
    },
    {
        value: 's3',
        label: 'S3 - Amazon Simple Storage Service'
    }
];
export default function ({ file, onConnect, onClose }) {
    const appSettings = useContext(AppSettingsContext);
    const [name, setName] = useState('');
    const [protocol, setProtocol] = useState('sftp');
    const [values, setValues] = useState({
        scheme: 'sftp',
        host: '',
        port: '',
        user: '',
        password: '',
        privatekey: ''
    });
    const [theme, setTheme] = useState(appSettings.theme);
    const [username, setUsername] = useState('');
    const [pass, setPass] = useState('');
    const [savePassword, setSavePassword] = useState(false);
    const [usePrivateKey, setUsePrivateKey] = useState(false);
    useEffect(() => {
        if (file?.length) {
            setName(parse(file).name);
            window.f5.get(file).then(config => {
                if (config) {
                    const { scheme, host, port, user, password, privatekey } = config;
                    setValues({
                        scheme,
                        host,
                        port: String(port),
                        user,
                        password,
                        privatekey: appSettings.isWin ? unixToWin(privatekey) : privatekey
                    });
                    setTheme(config.theme ?? appSettings.theme);
                    setSavePassword(password.length > 0);
                    setUsePrivateKey(privatekey.length > 0);
                }
            });
        }
    }, [file]);
    useEffect(() => reset(values), [values]);
    const { register, formState: { errors, isValid }, control, getValues, watch, reset, setValue } = useForm({ mode: 'all', values });
    useEffect(() => {
        const { unsubscribe } = watch(({ scheme, user, host, port, password }) => {
            setUsername(`${scheme}://${user}@${host}:${port ? port : (scheme == 'sftp' ? 22 : 21)}`);
            setProtocol(scheme);
            setPass(password);
        });
        return () => unsubscribe();
    }, [watch]);
    const parseHost = (host) => {
        const h = host.trim();
        const scheme = h.match(/^s?ftp:/);
        if (scheme) {
            return h.substring(scheme[0].length + 2);
        }
        if (protocol == 's3' && h.length > 8 && !h.match(/^https?:/)) {
            return `https://${h}`;
        }
        return h;
    };
    const buttons = [
        {
            id: ModalButtonID.Cancel,
            label: 'Cancel'
        },
        {
            id: 'save',
            label: 'Save',
            disabled: !isValid
        },
        {
            id: ModalButtonID.Ok,
            label: 'Connect',
            disabled: !isValid
        }
    ];
    const defaultPort = () => {
        return {
            sftp: '22',
            ftp: '21',
            s3: '443'
        }[protocol] ?? '';
    };
    const defaultKeyFile = '~/.ssh/id_ed25519';
    const onModalClose = async (id) => {
        if (id == 'save' || id == ModalButtonID.Ok) {
            const data = evolve({
                host: parseHost,
                port: p => parseInt(p || defaultPort()),
                user: trim
            }, { ...getValues(), theme });
            if (protocol == 'sftp') {
                if (usePrivateKey) {
                    if (data.privatekey) {
                        if (appSettings.isWin) {
                            data.privatekey = winToUnix(data.privatekey);
                        }
                    }
                    else {
                        data.privatekey = defaultKeyFile;
                    }
                }
                else {
                    data.privatekey = '';
                }
            }
            await window.f5.save(file, { ...data, savePassword });
            if (id == ModalButtonID.Ok) {
                onConnect(file);
            }
        }
        onClose();
        reset({ scheme: 'sftp', host: '', port: '', user: '', password: '', privatekey: '' });
        setTheme(appSettings.theme);
    };
    return React.createElement(React.Fragment, null, (file?.length || 0) > 0 &&
        React.createElement(Modal, { buttons: buttons, onClose: onModalClose, options: { okOnEnter: false, x: true } },
            React.createElement("form", { className: styles.root, onSubmit: e => e.preventDefault() },
                React.createElement("h1", null, name),
                React.createElement("label", null, "Protocol:"),
                React.createElement(Controller, { name: "scheme", control: control, render: ({ field }) => React.createElement(Select, { ...field, options: protocols }) }),
                React.createElement("ul", null,
                    React.createElement("li", null,
                        React.createElement("label", null,
                            protocol == 's3' ? 'URL' : 'Host',
                            ":"),
                        React.createElement("input", { className: classNames('dry', { error: errors.host }), ...register("host", { required: true }), placeholder: protocol == 's3' ? 'https://s3.amazonaws.com' : 'example.com', autoComplete: "off", onBlur: () => setValue('host', parseHost(getValues().host)) })),
                    React.createElement("li", null,
                        React.createElement("label", null, "Port:"),
                        React.createElement("input", { className: classNames('dry', { error: errors.port }), ...register("port", { pattern: /\d+/g }), placeholder: defaultPort(), autoComplete: "off" }))),
                React.createElement("label", null,
                    protocol == 's3' ? 'Access Key Id' : 'User',
                    ":"),
                React.createElement("input", { className: classNames('dry', { error: errors.user }), ...register("user", { required: true }), placeholder: protocol == 's3' ? 'unique identifier' : 'username', autoComplete: "off" }),
                React.createElement("div", { className: "username" },
                    React.createElement("input", { type: "text", name: "username", placeholder: "username", defaultValue: username, autoComplete: "username" })),
                (protocol != 'sftp' || !usePrivateKey) && React.createElement(React.Fragment, null,
                    React.createElement("label", null,
                        protocol == 's3' ? 'Secret Access Key' : 'Password',
                        ":"),
                    React.createElement(Controller, { name: "password", control: control, render: ({ field }) => React.createElement(Password, { ...field, placeholder: "Will ask if empty", autoComplete: 'current-password' }) })),
                (protocol != 'sftp' || !usePrivateKey) && pass && React.createElement(React.Fragment, null,
                    React.createElement("label", null),
                    React.createElement(Checkbox, { onChange: setSavePassword, value: savePassword }, "Save password on disk"),
                    React.createElement("p", null, "Passwords are stored in plain text. Use the browser password manager for a better protection.")),
                protocol == 'sftp' && React.createElement(React.Fragment, null,
                    React.createElement("label", null),
                    React.createElement(Checkbox, { onChange: setUsePrivateKey, value: usePrivateKey }, "Use SSH Private Key")),
                protocol == 'sftp' && usePrivateKey && React.createElement(React.Fragment, null,
                    React.createElement("label", null, "Key File Path:"),
                    React.createElement("input", { className: 'dry', ...register("privatekey"), placeholder: `${appSettings.isWin ? unixToWin(defaultKeyFile) : defaultKeyFile} (by default)`, autoComplete: "off" })),
                React.createElement("label", null, "Color Theme:"),
                React.createElement("div", { className: themesStyle.themes }, themes.map(t => React.createElement("span", { key: t, "data-mode": "dark", "data-theme": t, "data-active": t == theme, onClick: () => setTheme(t) }))))));
}
